import A11yConsentApi from '../a11y-consent-api';
import Option from '../option';
import mitt, {Emitter} from 'mitt';
import Logger from '../utilities/logger';

/**
 * An editable consent option. This is the link between a consent option model and the checkbox in the DOM.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export default class OptionControl {
  // Emits events related to the component
  public readonly emitter: Emitter<any> = mitt<any>();

  // A11y Consent API
  public readonly api: A11yConsentApi;

  // Consent option
  public readonly option: Option;

  // Checkbox linked to the consent option
  private readonly checkbox: HTMLInputElement;

  /**
   * @param {A11yConsentApi} api A11y Consent API
   * @param {Option} option Consent option
   * @param {HTMLInputElement} checkbox Checkbox linked to the consent option
   */
  constructor(api: A11yConsentApi, option: Option, checkbox: HTMLInputElement) {
    this.api = api;
    this.option = option;
    this.checkbox = checkbox;

    // Set initial state according to the current cookie value
    this.update();

    // Set up checkbox interaction
    this.checkbox.addEventListener('change', () => {
      this.emitter.emit('change', this);
    });

    // Watch for changes in the consent option
    this.option.emitter.on('enabled', () => {
      this.update();
    });
    this.option.emitter.on('disabled', () => {
      this.update();
    });
  }

  /**
   * Whether the checkbox is currently checked or not.
   *
   * @returns {boolean} True when the checkbox is checked, otherwise false
   */
  public isChecked(): boolean {
    return this.checkbox.checked;
  }

  /**
   * Sets the checked-state of the checkbox.
   *
   * @param {boolean} checked Checked state
   */
  public setChecked(checked: boolean): void {
    this.checkbox.checked = checked;
    if (checked) {
      this.emitter.emit('checked', this);
      Logger.info('option', `Marked "${this.option.identifier}" as "checked".`);
    } else {
      this.emitter.emit('unchecked', this);
      Logger.info('option', `Marked "${this.option.identifier}" as "unchecked".`);
    }
  }

  /**
   * Update the checkbox state according to the current consent settings of the user.
   */
  public update(): void {
    Logger.info(
      'option',
      `Updating checkbox state of option "${this.option.identifier}" to fit the current enabled state.`
    );
    this.setChecked(this.option.isEnabled() ?? false);
  }
}
